import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const ContactForm = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventType: '',
    eventDate: '',
    location: '',
    message: ''
  });

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.message) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Submit to backend
      const response = await axios.post(`${API}/contacts`, formData);
      
      console.log('Contact submitted successfully:', response.data);
      
      setSubmitSuccess(true);
      
      toast({
        title: "✅ Message Sent Successfully!",
        description: "Thank you for contacting us. We'll get back to you within 24 hours.",
        duration: 5000,
      });

      // Reset form after short delay
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          phone: '',
          eventType: '',
          eventDate: '',
          location: '',
          message: ''
        });
        setSubmitSuccess(false);
        setIsSubmitting(false);
      }, 5000);
      
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setIsSubmitting(false);
      toast({
        title: "Submission Error",
        description: "There was an issue sending your message. Please try again or email us directly.",
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      <div>
        <h3 className="text-2xl font-bold mb-6">Send us a message</h3>
        <div className="space-y-4">
          <div>
            <Label htmlFor="contact-name" className="mb-2 block">Full Name *</Label>
            <Input
              id="contact-name"
              type="text"
              placeholder="John Smith"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="contact-email" className="mb-2 block">Email *</Label>
              <Input
                id="contact-email"
                type="email"
                placeholder="john@example.com"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="contact-phone" className="mb-2 block">Phone *</Label>
              <Input
                id="contact-phone"
                type="tel"
                placeholder="07XXX XXXXXX"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="contact-eventType" className="mb-2 block">Event Type</Label>
              <Select value={formData.eventType} onValueChange={(value) => handleChange('eventType', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="wedding">Wedding</SelectItem>
                  <SelectItem value="birthday">Birthday Party</SelectItem>
                  <SelectItem value="corporate">Corporate Event</SelectItem>
                  <SelectItem value="anniversary">Anniversary</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="contact-eventDate" className="mb-2 block">Event Date</Label>
              <Input
                id="contact-eventDate"
                type="date"
                value={formData.eventDate}
                onChange={(e) => handleChange('eventDate', e.target.value)}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="contact-location" className="mb-2 block">Event Location</Label>
            <Input
              id="contact-location"
              type="text"
              placeholder="London or Hertfordshire"
              value={formData.location}
              onChange={(e) => handleChange('location', e.target.value)}
            />
          </div>

          <div>
            <Label htmlFor="contact-message" className="mb-2 block">Message *</Label>
            <Textarea
              id="contact-message"
              placeholder="Tell us about your event, specific requirements, or any questions you have..."
              value={formData.message}
              onChange={(e) => handleChange('message', e.target.value)}
              rows={5}
            />
          </div>

          <Button 
            type="button"
            onClick={handleSubmit}
            size="lg" 
            disabled={isSubmitting || submitSuccess}
            className="w-full bg-emerald-700 hover:bg-emerald-800 text-white disabled:opacity-50"
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Sending...
              </>
            ) : submitSuccess ? (
              <>
                <svg className="mr-2 h-5 w-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                  <path d="M5 13l4 4L19 7"></path>
                </svg>
                Message Sent!
              </>
            ) : (
              <>
                <Send className="mr-2 h-5 w-5" />
                Send Message
              </>
            )}
          </Button>
        </div>
      </div>

      <div>
        <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
        <div className="space-y-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail className="h-6 w-6 text-emerald-700" />
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Email Us</h4>
                  <a href="mailto:info@myselfiemoments.co.uk" className="text-gray-600 hover:text-emerald-700 transition-colors">
                    info@myselfiemoments.co.uk
                  </a>
                  <p className="text-sm text-gray-500 mt-1">We'll respond within 24 hours</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Phone className="h-6 w-6 text-emerald-700" />
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Call or Text</h4>
                  <p className="text-gray-600">Use the live chat or contact form</p>
                  <p className="text-sm text-gray-500 mt-1">Available 7 days a week</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-6 w-6 text-emerald-700" />
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Service Areas</h4>
                  <p className="text-gray-600">London & Hertfordshire</p>
                  <p className="text-sm text-gray-500 mt-1">Travel to other areas available</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-emerald-50 border-emerald-200">
            <CardContent className="pt-6">
              <h4 className="font-semibold mb-3">Why Choose Us?</h4>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-emerald-700 rounded-full"></span>
                  Professional attendants at every event
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-emerald-700 rounded-full"></span>
                  Unlimited high-quality captures
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-emerald-700 rounded-full"></span>
                  Instant digital delivery
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-emerald-700 rounded-full"></span>
                  Flexible packages & add-ons
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-emerald-700 rounded-full"></span>
                  Serving London & Hertfordshire
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ContactForm;